// 左右の色（IS_SEL）（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { C } from './core/model.js';
import { txt, txtHtml } from './text.js';
import { narrowUI, tallUI } from './store.js';
import { updateSide } from './side.js';
import { $, keyOf, sideDot } from './util.js';
import { canPattern } from './core/pattern.js';
import { fold } from './colors.js';
import { groupOf, isLinked } from './colorlink.js';
import { openColorPicker } from './picker.js';
import type { Category, PartState } from './types.js';
import { CONFIG } from './config.js';
import { catByKey } from './store.js';
import { partsList } from './core/data.js';
import { on } from './core/events.js';
import { choosePart } from './parts.js';
import { buildPatterns, openColorSettings, paletteColors, paletteKind, paletteName, paletteOf, setColor } from './palette.js';
export const LR_LABELS = ['lr_both', 'lr_left', 'lr_right'];
// 左右の色が使えるカテゴリか（データの IS_SEL）。メーカーの設定 lr: false なら出さない（GENESIS2・DRESSUP2: クラシック版にも LR のボタンが無く、
// めなどが左右で別のカテゴリになっているので、1 つのカテゴリの中の「左・右」はほとんど効かない。2026-10-27、ユーザーの指摘）
export function lrOn(cat: Category): boolean { return CONFIG.lr !== false && cat.IS_SEL > 0; }
// 色を選べるカテゴリか（データに色かグラデーションがある）。設定 noColor のカテゴリは出さない（KAWAII2 のフィルター: データに色が 1 つあるが、描くときに使わない。2026-10-28）
const NO_COLOR = CONFIG.noColor || [];
export function colorUsable(cat: Category): boolean { return cat.NAME !== 'chara' && NO_COLOR.indexOf(keyOf(cat)) < 0 && !!(cat.COLOR.length || cat.GRAD.length > 1); }
// （左右の2つの g の見分け方は charat.draw.js の colorLayer。パーツの中の layer > g > g > g、無ければ layer > g > g の2つ）
// 色の表示用（元の色そのものは「元の色」として扱う）
export function shownColor(cat: Category, c: string | null | undefined) { c = (c || '').toLowerCase(); return cat.COLOR.length && c === cat.COLOR[0].toLowerCase() ? '' : c; }
export function currentColor(cat: Category, st: PartState | undefined): string | null {
  if (!st) return '';
  if (lrOn(cat) && S.lrSel) return shownColor(cat, (S.lrSel === 1 ? st.lfrom : st.rfrom) || st.col);
  if (S.grad && S.grad.key === keyOf(cat)) {
    // 選んでいるつまみの色（上を優先）
    if (S.grad.sel.from) return st.from ? S.grad.from : '';
    if (S.grad.sel.to) return st.to ? S.grad.to : '';
    return null;
  }
  return shownColor(cat, st.col);
}
// ---- 「ふく」の中のリボンなど（設定 subTabs、2026-10-29〜 KAWAII。ユーザー「リボンを別カテゴリにしているけど分かりにくい」） ----
// 設定 subTabs: { 選んでいるカテゴリのキー: { key: 付けるカテゴリのキー, label: 切りかえの名前の文言のキー, needs: いまのキャラにその所があるときだけ出す（CSS のセレクタ） } }。
// 色の欄の「色・柄」に切りかえを足し、押すと 付けない・付ける と、そのカテゴリの色の一覧（塗る相手をそのカテゴリに）。そのカテゴリは列に出さない（hideCats）。
// 保存データはそのカテゴリの行のまま（クラシック版と行き来できる）。全身のメーカーのスマホ（tallUI）では出さない
type SubTab = { key: string; label: string; needs?: string };
const SUB_TABS: Record<string, SubTab> = CONFIG.subTabs || {};
function subOf(cat: Category): { tab: SubTab; cat: Category } | null {
  const t = SUB_TABS[keyOf(cat)];
  if (!t || tallUI) return null;
  const sc = catByKey[t.key];
  if (!sc || !partsList(sc.NAME).length) return null;
  if (t.needs && !(C.svg && C.svg.querySelector(t.needs))) return null;
  return { tab: t, cat: sc };
}
function subToggle(cat: Category, sub: { tab: SubTab; cat: Category }, noneLabel = 'part_none') {
  const sc = sub.cat, skey = keyOf(sc), list = partsList(sc.NAME), sst = C.state[skey];
  const none = list[0], onPart = list.filter((p) => Number(p.id) === 2)[0] || list[1];
  const isOn = !!sst && sst.parts != null && Number(sst.parts) !== Number(none.id);
  const row = document.createElement('div');
  row.className = 'lr sub-toggle';
  row.setAttribute('role', 'radiogroup');
  row.setAttribute('aria-label', txt(sub.tab.label));
  ([[false, noneLabel], [true, 'sub_on']] as [boolean, string][]).forEach(([want, label]) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = isOn === want ? 'on' : '';
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', isOn === want ? 'true' : 'false');
    if (want) {
      const dot = document.createElement('i');
      dot.style.background = (sst && sst.col) || sc.COLOR[0] || '#ccc';
      b.appendChild(dot);
    }
    b.appendChild(document.createTextNode(txt(label)));
    b.addEventListener('click', () => {
      if (isOn !== want && onPart) choosePart(sc, want ? onPart : none, list);
      buildColors(cat);
    });
    row.appendChild(b);
  });
  return row;
}
// スマホ: 一覧の上に「リボン［はずす］［●つける］」の行（色の帯の切りかえの中だと気づきにくかった。ユーザーの実機の指摘 2026-10-29）。
// 色は今までどおり色の帯の「リボン」で変える。全身のメーカーのスマホ（tallUI）と PC は今までどおり
let subRowEl: HTMLElement | null = null;
function subRowOn() { return narrowUI.matches && !tallUI; }
function updateSubRow(cat: Category, sub: { tab: SubTab; cat: Category } | null) {
  if (!sub || !subRowOn()) { if (subRowEl) subRowEl.hidden = true; return; }
  if (!subRowEl) {
    subRowEl = document.createElement('div');
    subRowEl.className = 'sub-row';
    subRowEl.id = 'subRow';
    $('parts').before(subRowEl);
  }
  subRowEl.hidden = false;
  subRowEl.textContent = '';
  const name = document.createElement('span');
  name.className = 'sub-row-name';
  name.textContent = txt(sub.tab.label);
  subRowEl.appendChild(name);
  subRowEl.appendChild(subToggle(cat, sub, 'sub_off'));
}
// 選んでいるカテゴリのパーツが替わったら（リボンの所の有る無しが変わる）、色の欄を作り直す
on('drawn', (dcat, _st, why) => {
  if (why === 'color' || why === 'pattern') return;
  const sel = catByKey[S.selKey];
  if (!sel || !SUB_TABS[S.selKey]) return;
  const k = keyOf(dcat);
  if (k === S.selKey || k === SUB_TABS[S.selKey].key) buildColorStrip(sel);
});
let stripRO: ResizeObserver | null = null; // 色の帯の高さを見る（帯は描くたびに作り直すので、前の帯の分は外す）
export function buildColors(cat: Category) {
  buildColorStrip(cat);
  updateSide(); // 全身のメーカーのスマホの右の列（色・柄・左右）をそろえる
}
// 全身のメーカーのスマホ（tallUI）は、色・柄の切りかえと左右の色を右の列（side.ts）に出すので、ここには作らない
function buildColorStrip(cat: Category) {
  updateSubRow(cat, C.state[keyOf(cat)] ? subOf(cat) : null); // スマホの一覧の上のリボンの行（色の無いカテゴリでも消すので、先に）
  const box = $('colors');
  const prevStrip = box.querySelector<HTMLElement>('.sw-strip'), keepScroll = prevStrip ? prevStrip.scrollTop : 0; // 色を選ぶたびに作り直すので、帯のスクロールの位置は引きつぐ
  box.textContent = '';
  $('colorsFold').classList.remove('strip');
  const usable = colorUsable(cat);
  fold('colorsFold', !!usable);
  if (!usable) return;
  const sub = C.state[keyOf(cat)] ? subOf(cat) : null;
  if (canPattern(cat) && C.state[keyOf(cat)] && tallUI) {
    if (S.colorTab === 'pattern') { buildPatterns(cat, box); return; }
  } else if ((canPattern(cat) && C.state[keyOf(cat)]) || sub) {
    // 色・柄（柄を付けられるとき）・リボンなど（設定 subTabs。いまのパーツに付ける所があるとき）の切りかえ
    const tabs = document.createElement('div');
    tabs.className = 'lr ctabs';
    tabs.setAttribute('role', 'radiogroup');
    tabs.setAttribute('aria-label', txt('color_tabs'));
    const list: [string, string][] = [['color', 'tab_color']];
    if (canPattern(cat) && C.state[keyOf(cat)]) list.push(['pattern', 'tab_pattern']);
    if (sub) list.push(['sub', sub.tab.label]);
    if (!list.some((t) => t[0] === S.colorTab)) S.colorTab = 'color';
    list.forEach((t) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = S.colorTab === t[0] ? 'on' : '';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', S.colorTab === t[0] ? 'true' : 'false');
      b.textContent = txt(t[1]);
      b.addEventListener('click', () => { S.colorTab = t[0]; buildColors(cat); });
      tabs.appendChild(b);
    });
    box.appendChild(tabs);
    if (S.colorTab === 'pattern') { buildPatterns(cat, box); return; }
  }
  // リボンなど（S.colorTab === 'sub'）: 付ける・付けないと、そのカテゴリの色。下の色の一覧は塗る相手（target）をそのカテゴリにして作る
  const onSub = !!sub && S.colorTab === 'sub';
  const target = onSub ? sub!.cat : cat, tkey = keyOf(target);
  const now = currentColor(target, C.state[tkey]);
  if (onSub && !subRowOn()) box.appendChild(subToggle(cat, sub!)); // スマホは一覧の上の行で付け外しするので、帯には色だけ
  // 左右の色が使えるカテゴリ（め）: どこに色を付けるか。それぞれの今の色を小さな丸で
  if (!onSub && lrOn(cat) && C.state[keyOf(cat)] && !tallUI) {
    const st0 = C.state[keyOf(cat)];
    const lr = document.createElement('div');
    lr.className = 'lr';
    lr.setAttribute('role', 'radiogroup');
    lr.setAttribute('aria-label', txt('lr_where'));
    LR_LABELS.forEach((label, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = S.lrSel === i ? 'on' : '';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', S.lrSel === i ? 'true' : 'false');
      const dot = document.createElement('i');
      // 両方は左と右の半分ずつ（2026-10-28 ユーザー「靴下は半分ずつ変わるのに、目はそうなっていない」。前は両方の色だけ）
      dot.style.background = sideDot(i, st0.lfrom || st0.col || cat.COLOR[0], st0.rfrom || st0.col || cat.COLOR[0]);
      b.appendChild(dot);
      b.appendChild(document.createTextNode(txt(label)));
      b.addEventListener('click', () => { S.lrSel = i; buildColors(cat); });
      lr.appendChild(b);
    });
    box.appendChild(lr);
  }
  // 色の設定（パレットの切りかえ・連動）を開くボタン。今のパレットの名前と、連動しているときはその数
  const kind = paletteKind(target), pal = paletteOf(kind);
  const chip = document.createElement('button');
  chip.type = 'button';
  chip.className = 'pal-chip';
  chip.title = txt('color_settings');
  chip.setAttribute('aria-haspopup', 'dialog');
  chip.innerHTML = '<svg class="pal-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.2 0 1.7-.8 1.7-1.6 0-1.4-1.2-1.6-1.2-2.8 0-1 .8-1.6 1.8-1.6h2.2a4 4 0 0 0 4-4C20.5 6.6 16.7 3.5 12 3.5z"/><circle class="dot" cx="7.6" cy="11" r="1.3"/><circle class="dot" cx="10" cy="7.3" r="1.3"/><circle class="dot" cx="14.4" cy="7.3" r="1.3"/></svg>' +
    '<span>' + txtHtml(paletteName(kind, pal)) + '</span>' +
    (isLinked(target) ? '<i class="pal-link" title="' + txtHtml('link_title') + '"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1"/></svg>' + groupOf(target).length + '</i>' : '') +
    '<svg class="caret" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10l5 5 5-5"/></svg>' +
    '<small class="pal-cap">' + txtHtml('side_colorset') + '</small>' + // 全身のスマホの左の器の上だけ出す短い名前（右の列のボタンと同じ形。ユーザー 2026-10-28「押せそうに見えない・設定と分からない」）
    '<svg class="pal-gear" viewBox="0 0 24 24" aria-hidden="true"><path fill-rule="evenodd" d="M10.06 4.24L10.52 1.5 L13.48 1.5 L13.94 4.24A8 8 0 0 1 16.12 5.14L18.38 3.53 L20.47 5.62 L18.86 7.88A8 8 0 0 1 19.76 10.06L22.5 10.52 L22.5 13.48 L19.76 13.94A8 8 0 0 1 18.86 16.12L20.47 18.38 L18.38 20.47 L16.12 18.86A8 8 0 0 1 13.94 19.76L13.48 22.5 L10.52 22.5 L10.06 19.76A8 8 0 0 1 7.88 18.86L5.62 20.47 L3.53 18.38 L5.14 16.12A8 8 0 0 1 4.24 13.94L1.5 13.48 L1.5 10.52 L4.24 10.06A8 8 0 0 1 5.14 7.88L3.53 5.62 L5.62 3.53 L7.88 5.14A8 8 0 0 1 10.06 4.24ZM8.6 12a3.4 3.4 0 1 0 6.8 0a3.4 3.4 0 1 0 -6.8 0Z"/></svg>'; // 同じく全身のスマホだけ、パレットの絵の代わりに歯車（ユーザー「まだわかりにくいので歯車に」）
  chip.addEventListener('click', () => { openColorSettings(target); });
  box.appendChild(chip);
  // スマホ: 色の設定は絵だけにし、色・柄の切りかえ・左右の色と一緒に左に固定（横にスクロールしても見える）。元の色・自由な色はその右、続けて色
  // （ユーザー 2026-10-12。PC と同じ並び。前は元の色・自由な色を色の後ろ＝右端に回していた）
  const narrow = narrowUI.matches;
  let fixed: HTMLElement | null = null;
  if (narrow) {
    chip.classList.add('icon');
    fixed = document.createElement('div');
    fixed.className = 'colors-fixed';
    // リボンなどの「なし・つける」は固定にしない（色・柄・リボンの切りかえと並ぶと色の丸が見えなくなる。色と一緒に横に流す。2026-10-29）
    Array.prototype.slice.call(box.children).forEach((n: Element) => { if (!n.classList.contains('sub-toggle')) fixed!.appendChild(n); });
    box.insertBefore(fixed, box.firstChild);
  }
  // 全身のメーカーのスマホ: 色は1本の帯（.sw-strip、ペイントチップのように色で区切る）にまとめ、パレットの切りかえと自由な色はその上に分ける
  // （帯の上には重ねない）。帯の上下は、続きがあるときだけふわっと消す（2026-10-28 ユーザー「丸が独立してうるさい」「端を切らずにフェード」「選択はリングで」）
  const strip = tallUI ? document.createElement('div') : box;
  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'sw reset' + (now === '' ? ' sel' : '');
  reset.title = txt('color_default');
  reset.addEventListener('click', () => { setColor('', false, tkey); });
  (tallUI && fixed ? fixed : strip).appendChild(reset); // 全身のスマホは色ではないもの（パレットの切りかえ・自由な色・元の色）を帯の上にまとめる
  // 自由な色（自前の色選び openColorPicker。前はブラウザの部品 input type=color）
  const custom = document.createElement('button');
  custom.type = 'button';
  custom.className = 'sw custom';
  custom.title = txt('color_custom');
  custom.setAttribute('aria-label', txt('color_custom'));
  custom.setAttribute('aria-haspopup', 'dialog');
  custom.addEventListener('click', () => { openColorPicker(target, now, tkey); });
  (tallUI && fixed ? fixed : box).appendChild(custom);
  const hexes = paletteColors(target, pal);
  if (!hexes.length) {
    const empty = document.createElement('span');
    empty.className = 'pal-empty';
    empty.textContent = txt('pal_empty');
    strip.appendChild(empty);
  }
  hexes.forEach((hex) => {
    const sw = document.createElement('button');
    sw.type = 'button';
    sw.className = 'sw' + (hex === now ? ' sel' : '');
    sw.style.background = hex;
    sw.title = hex;
    sw.addEventListener('click', () => { setColor(hex, false, tkey); });
    strip.appendChild(sw);
  });
  if (strip !== box) {
    strip.className = 'sw-strip';
    box.appendChild(strip);
    $('colorsFold').classList.add('strip');
    strip.scrollTop = keepScroll;
    const fade = () => {
      strip.classList.toggle('more-up', strip.scrollTop > 2);
      strip.classList.toggle('more-down', strip.scrollTop + strip.clientHeight < strip.scrollHeight - 2);
    };
    strip.addEventListener('scroll', fade, { passive: true });
    // 帯の高さが変わったとき（パーツの一覧をしまう・出す、窓の大きさ）も掛け直す。前は描いたときだけで、しまったあとも下がふわっと消えたままだった（2026-10-28）
    if (stripRO) stripRO.disconnect();
    if (typeof ResizeObserver !== 'undefined') { stripRO = new ResizeObserver(fade); stripRO.observe(strip); }
    fade();
  }
}
